import { createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import {
  showLoadingScreen,
  setSnackBar,
} from "../../reusable_components/site_data/siteDataSlice";
import { backend_url } from "../../App";

export const cameraSlice = createSlice({
  name: "cameras",
  initialState: {
    cameras: {
      count: 0,
      next: null,
      previous: null,
      results: [],
      img: null,
    },
    loading: false,
    error: null,
  },
  reducers: {
    setCameras: (state, action) => {
      state.cameras.count = action.payload.count;
      state.cameras.next = action.payload.next;
      state.cameras.previous = action.payload.previous;
      state.cameras.results = action.payload.results.map((camera) => ({
        ...camera,
        lastRecordedImage: null,
      }));
      state.cameras.img = state.cameras.results;
      state.error = null;
    },
    setLastRecordedImage: (state, action) => {
      const { cameraId, imageUrl } = action.payload;
      const cameraIndex = state.cameras.results.findIndex(
        (camera) => camera.id === cameraId,
      );
      if (cameraIndex !== -1) {
        state.cameras.results[cameraIndex].lastRecordedImage = imageUrl;
      }
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    clearCameras: (state) => {
      state.cameras = {
        count: 0,
        next: null,
        previous: null,
        results: [],
        img: null,
      };
      state.error = null;
    },
  },
});

// Helper to get auth header
const getAuthHeader = () => ({
  Authorization: `Token ${localStorage.getItem("token")}`,
});

// Async thunk for fetching cameras with fallback
export const getCameras = () => async (dispatch, getState) => {
  dispatch(showLoadingScreen(true));
  dispatch(setLoading(true));
  dispatch(setError(null));

  try {
    const config = {
      headers: getAuthHeader(),
    };

    // Try to fetch cameras
    const response = await axios.get(`${backend_url}/core/api/camera/`, config);

    // If successful, set cameras
    dispatch(setCameras(response.data));

    // Fetch latest images for each camera (optimized)
    const cameras = response.data.results || [];

    if (cameras.length > 0) {
      // Use Promise.allSettled to handle individual failures
      const imagePromises = cameras.map(async (camera) => {
        try {
          const eventResponse = await fetch(
            `${backend_url}/core/api/event/?cameras=${camera.id}&page_size=1`,
            {
              headers: getAuthHeader(),
            },
          );

          if (!eventResponse.ok) {
            throw new Error(`HTTP error: ${eventResponse.status}`);
          }

          const eventData = await eventResponse.json();
          const events = eventData.results || [];

          if (events.length > 0 && events[0].thumbnail) {
            dispatch(
              setLastRecordedImage({
                cameraId: camera.id,
                imageUrl: events[0].thumbnail,
              }),
            );
          }
        } catch (error) {
          console.error(
            `Failed to fetch image for camera ${camera.id}:`,
            error,
          );
          // Don't dispatch error for individual image failures
        }
      });

      // Wait for all image fetches to complete (but don't block)
      await Promise.allSettled(imagePromises);
    }
  } catch (error) {
    console.error("Error fetching cameras:", error);

    let errorMessage = "Failed to load cameras";

    if (error.response) {
      // Check for the specific NotSupportedError
      const responseData = error.response.data;
      if (
        typeof responseData === "string" &&
        responseData.includes("NotSupportedError")
      ) {
        errorMessage = "Backend compatibility issue. Using fallback data.";
        // Try fallback - fetch cameras without the problematic query
        try {
          // Fallback: try to fetch cameras with a simpler query
          const fallbackResponse = await axios.get(
            `${backend_url}/core/api/camera/?limit=100`,
            { headers: getAuthHeader() },
          );
          dispatch(setCameras(fallbackResponse.data));
          errorMessage = null; // Clear error if fallback worked
        } catch (fallbackError) {
          errorMessage = "Unable to load cameras. Please contact support.";
        }
      } else if (responseData?.detail) {
        errorMessage = responseData.detail;
      } else if (responseData?.error) {
        errorMessage = responseData.error;
      }
    } else if (error.request) {
      errorMessage = "No response from server. Please check your connection.";
    } else {
      errorMessage = error.message;
    }

    if (errorMessage) {
      dispatch(setError(errorMessage));
      dispatch(setSnackBar(errorMessage));
    }
  } finally {
    dispatch(showLoadingScreen(false));
    dispatch(setLoading(false));
  }
};

// Action creators are generated for each case reducer function
export const {
  setCameras,
  setLastRecordedImage,
  setLoading,
  setError,
  clearCameras,
} = cameraSlice.actions;

export const selectCameras = (state) => state.cameras.cameras;
export const selectCamerasLoading = (state) => state.cameras.loading;
export const selectCamerasError = (state) => state.cameras.error;

export default cameraSlice.reducer;
